import type {
  TemplateListItemView,
  TemplatePreviewView,
  TemplatePromoView,
  TemplateUnknownView,
} from "@planerius/contracts/cabinet-notifications"

import { TEMPLATE_PLACEHOLDERS } from "@planerius/contracts/notification-templates"

import { formatMoney } from "../../directories/format-money"
import { plural } from "../../../lib/plural"

/**
 * 4-Б6 «Шаблоны сообщений» — подписи и решения экрана, отдельно от разметки.
 *
 * ЗДЕСЬ ЖИВЁТ ГЛАВНОЕ РЕШЕНИЕ ЭКРАНА: КОГДА «СОХРАНИТЬ» НЕАКТИВНА. Текст с неизвестной
 * подстановкой уйдёт клиенту с дырой посреди предложения, и салон узнает об этом
 * от клиента. Поэтому кнопка гаснет, а рядом стоит объяснение с названием ближайшей
 * существующей подстановки — не «ошибка в тексте», а «возможно, вы имели в виду {мастер}».
 *
 * ЦЕНА НАЗЫВАЕТСЯ ДИАПАЗОНОМ, А НЕ ЧИСЛОМ. Провайдер SMS не выбран, у двух провайдеров
 * цена сегмента разная, и одна цифра здесь — это сумма, которой не будет, а салон
 * по ней посчитает месячный бюджет.
 */

/** Каналы в том порядке, в каком их показывает экран. */
export const TEMPLATE_CHANNELS: readonly { readonly value: string; readonly label: string }[] = [
  { value: "sms", label: "SMS" },
  { value: "telegram", label: "Telegram" },
  { value: "max", label: "MAX" },
  { value: "push", label: "Пуш" },
  { value: "email", label: "Письмо" },
]

/** «1 часть», «2 части», «5 частей» — как в макете. */
export function partsWord(parts: number): string {
  if (parts === 1) return "1 часть"
  return parts < 5 ? `${String(parts)} части` : `${String(parts)} частей`
}

/**
 * Строка стоимости: «96 знаков — 2 части, от 7,00 до 16,00 ₽ за отправку».
 *
 * БЕСПЛАТНЫЙ КАНАЛ НЕ ПОКАЗЫВАЕТ ЦЕНУ ВОВСЕ. Ноль рублей рядом с текстом для Telegram
 * — это лишняя строка, которая заставляет искать подвох; а главное, она обесценивает
 * ту же строку в SMS, где цена настоящая.
 */
export function costLine(preview: TemplatePreviewView): string {
  const head = `${String(preview.characters)} ${plural(preview.characters, "знак", "знака", "знаков")}`
  if (preview.costMinorMax === 0) {
    return `${head} — канал бесплатный.`
  }
  const from = formatMoney(preview.costMinorMin) ?? "—"
  const to = formatMoney(preview.costMinorMax) ?? "—"
  const price =
    preview.costMinorMin === preview.costMinorMax ? from : `от ${from} до ${to}`
  return `${head} — ${partsWord(preview.segments)}, ${price} за отправку.`
}

/**
 * Подсказка под строкой стоимости.
 *
 * НАЗЫВАЕТ, СКОЛЬКО ЗНАКОВ СОКРАТИТЬ. «Слишком длинно» не помогает: салон не знает,
 * на сколько именно резать, и режет либо мало, либо половину смысла.
 */
export function costHint(preview: TemplatePreviewView): string {
  if (preview.costMinorMax === 0) {
    return "В этом канале место бесплатно: длина на цену не влияет."
  }
  if (preview.segments <= 1) {
    return "Кириллица режет SMS по 70 знаков — вы в пределах одной части."
  }
  // 67 знаков в каждой части многосегментного, 70 — в односегментном (UCS-2).
  const fits = preview.segments === 2 ? 70 : (preview.segments - 1) * 67
  const cut = Math.max(1, preview.characters - fits)
  return `Сократите на ${String(cut)} ${plural(cut, "знак", "знака", "знаков")} — будет ${partsWord(preview.segments - 1)}. Кириллица режет SMS по 70 знаков.`
}

/** Тон строки стоимости: одна часть — спокойно, больше — предупреждение (макет). */
export function costTone(preview: TemplatePreviewView): "ok" | "warn" {
  return preview.costMinorMax > 0 && preview.segments > 1 ? "warn" : "ok"
}

/** Откуда текст: «Наш текст по умолчанию» или «Изменён салоном · Ирина В., сегодня». */
export function originLine(item: TemplateListItemView, today: string): string {
  if (item.origin === "system") return "Наш текст по умолчанию"
  const who = item.updatedBy === null ? "" : ` · ${shortName(item.updatedBy)}`
  const when = item.updatedAt === null ? "" : `, ${whenLabel(item.updatedAt, today)}`
  return `Изменён салоном${who}${when}`
}

/** «Ирина Волкова» → «Ирина В.»: фамилия целиком в строку не влезает и не нужна. */
function shortName(name: string): string {
  const parts = name.trim().split(/\s+/u)
  const first = parts[0] ?? name
  const last = parts[1]
  return last === undefined ? first : `${first} ${last.slice(0, 1)}.`
}

function whenLabel(instant: string, today: string): string {
  const date = instant.slice(0, 10)
  if (date === today) return "сегодня"
  const [year, month, day] = date.split("-")
  return `${day ?? ""}.${month ?? ""}.${year ?? ""}`
}

/**
 * Фраза о неизвестной подстановке — дословно из макета.
 *
 * Собирается здесь, а не берётся из ответа сервера: та же фраза нужна В РЕДАКТОРЕ,
 * по ходу набора, когда запроса ещё не было. Правила при этом одни — из контрактов.
 */
export function unknownLine(item: TemplateUnknownView): string {
  const head = `В тексте есть подстановка {${item.raw}}, которой у нас нет`
  const tail = "Пока не исправите, шаблон не сохранится и продолжит работать прежний."
  return item.suggestion === null
    ? `${head}. ${tail}`
    : `${head} — возможно, вы имели в виду {${item.suggestion}}. ${tail}`
}

/** Фраза про рекламу — дословно из макета, вместе с цитатой. */
export function promoLine(item: TemplatePromoView): string {
  return (
    `Похоже на рекламу: «${item.quote}». Напоминание о записи с рекламой внутри ` +
    "считается рекламной рассылкой, и отправлять её можно только тем, кто дал согласие " +
    "на рекламу."
  )
}

/**
 * Можно ли сохранять.
 *
 * ТОЛЬКО НЕИЗВЕСТНАЯ ПОДСТАНОВКА ЗАПРЕЩАЕТ СОХРАНЕНИЕ. Реклама — предупреждение:
 * отличить рекламу от уместного упоминания автоматически нельзя («скидка постоянного
 * клиента уже учтена» — не реклама, а часть расчёта), и решение остаётся за салоном.
 * Длина тоже не запрещает, кроме предела SMS, — за длину салон платит сознательно.
 */
export function canSave(input: {
  readonly preview: TemplatePreviewView | null
  readonly dirty: boolean
  readonly saving: boolean
  /** Подстановки, которые этому событию разрешены: русские имена из ответа сервера. */
  readonly notAllowed?: readonly string[]
}): boolean {
  if (!input.dirty || input.saving) return false
  if ((input.notAllowed ?? []).length > 0) return false
  if (input.preview === null) return true
  return input.preview.unknown.length === 0
}

/**
 * Подстановки в тексте, которые ЭТОМУ событию не разрешены.
 *
 * ПОЧЕМУ ЭТО ПРОВЕРЯЕТСЯ НА ЭКРАНЕ, А НЕ ТОЛЬКО НА СЕРВЕРЕ. Кнопка была активна:
 * человек вставлял `{ссылка_отписки}` в напоминание о визите, жал «Сохранить» и получал
 * отказ — а какая именно подстановка виновата, ему не говорили. Чипы при этом показывают
 * только разрешённые, но текст можно набрать и руками, и принести из прошлой версии.
 *
 * Разрешённые приходят от сервера (`placeholders` в ответе), поэтому два перечня
 * разойтись не могут: он один.
 */
export function notAllowedPlaceholders(
  body: string,
  allowed: readonly { readonly ru: string }[],
): readonly string[] {
  const permitted = new Set(allowed.map((item) => item.ru))
  const known = new Set(TEMPLATE_PLACEHOLDERS.map((item) => item.ru))
  const found = new Set<string>()
  for (const match of body.matchAll(/\{\s*([^{}\n]{1,60}?)\s*\}/gu)) {
    const name = (match[1] ?? '').trim().toLowerCase().replaceAll(' ', '_').replaceAll('-', '_')
    // Неизвестные разбираются отдельно и своим объяснением: здесь только те, что
    // существуют, но этому событию не положены.
    if (known.has(name) && !permitted.has(name)) found.add(name)
  }
  return [...found]
}

/** Фраза об этом — по-русски, а не внутренним ключом: салон ключей не набирал. */
export function notAllowedLine(names: readonly string[]): string {
  const list = names.map((name) => `{${name}}`).join(', ')
  return `Для этого события нельзя использовать ${list}: в таком сообщении этих данных ещё нет. Уберите их, чтобы сохранить.`
}

/** Заголовок шаблона в списке: «Напоминание о визите · SMS». */
export function templateTitle(item: TemplateListItemView): string {
  const channel = TEMPLATE_CHANNELS.find((row) => row.value === item.channel)?.label ?? item.channel
  return `${item.title} · ${channel}`
}

/** «Версии · 3». Пусто, если версий нет: кнопка, открывающая пустоту, хуже отсутствия. */
export function versionsLabel(item: TemplateListItemView): string | null {
  return item.versions === 0 ? null : `Версии · ${String(item.versions)}`
}
